/**
 * Languoid codes — ISO 639-3 + script suffix (+ optional disambiguating suffix).
 *
 * Format: `<iso639-3>_<script>`, e.g. `swh_Latn` for Swahili in Latin script,
 * `npi_Deva` for Nepali in Devanagari. A trailing `_<suffix>` disambiguates a
 * variety sharing an iso+script — e.g. `eng_Latn_aust1313` for Australian
 * English (glottocode suffix).
 *
 * This IS the active project language catalog (the languages teams work with).
 * It seeds the live DB `Languoid` catalog; project admins can still add more at
 * runtime (desktop Configuration → Add language). To change the set, edit this
 * list and re-run the seed (it prunes catalog rows no longer present here).
 */

export interface Languoid {
  code: string; // e.g. "npi_Deva"
  iso639_3: string; // e.g. "npi"
  script: string; // e.g. "Deva"
  englishName: string; // e.g. "Nepali"
  endonym?: string; // optional self-name
}

export const STARTER_LANGUOIDS: readonly Languoid[] = [
  { code: 'ukr_Cyrl', iso639_3: 'ukr', script: 'Cyrl', englishName: 'Ukrainian' },
  { code: 'eng_Latn_aust1313', iso639_3: 'eng', script: 'Latn', englishName: 'Australian English' },
  { code: 'som_Latn', iso639_3: 'som', script: 'Latn', englishName: 'Somali' },
  { code: 'hun_Latn', iso639_3: 'hun', script: 'Latn', englishName: 'Hungarian' },
  { code: 'uzn_Latn', iso639_3: 'uzn', script: 'Latn', englishName: 'Northern Uzbek' },
  { code: 'nob_Latn', iso639_3: 'nob', script: 'Latn', englishName: 'Norwegian Bokmål' },
  { code: 'srp_Cyrl', iso639_3: 'srp', script: 'Cyrl', englishName: 'Serbian' },
  { code: 'jav_Latn', iso639_3: 'jav', script: 'Latn', englishName: 'Javanese' },
  { code: 'sun_Latn', iso639_3: 'sun', script: 'Latn', englishName: 'Sundanese' },
  { code: 'amh_Ethi', iso639_3: 'amh', script: 'Ethi', englishName: 'Amharic' },
  { code: 'mya_Mymr', iso639_3: 'mya', script: 'Mymr', englishName: 'Burmese' },
  { code: 'khm_Khmr', iso639_3: 'khm', script: 'Khmr', englishName: 'Central Khmer' },
  { code: 'fin_Latn', iso639_3: 'fin', script: 'Latn', englishName: 'Finnish' },
  { code: 'lao_Laoo', iso639_3: 'lao', script: 'Laoo', englishName: 'Lao' },
  { code: 'zsm_Latn', iso639_3: 'zsm', script: 'Latn', englishName: 'Malay' },
  { code: 'swh_Latn', iso639_3: 'swh', script: 'Latn', englishName: 'Swahili', endonym: 'Kiswahili' },
  { code: 'zul_Latn', iso639_3: 'zul', script: 'Latn', englishName: 'Zulu' },
  { code: 'asm_Beng', iso639_3: 'asm', script: 'Beng', englishName: 'Assamese' },
  { code: 'mal_Mlym', iso639_3: 'mal', script: 'Mlym', englishName: 'Malayalam' },
  { code: 'npi_Deva', iso639_3: 'npi', script: 'Deva', englishName: 'Nepali' },
  { code: 'ory_Orya', iso639_3: 'ory', script: 'Orya', englishName: 'Oriya' },
  { code: 'hau_Latn', iso639_3: 'hau', script: 'Latn', englishName: 'Hausa', endonym: 'Hausa' },
] as const;

const codeIndex = new Map(STARTER_LANGUOIDS.map((l) => [l.code, l] as const));

export const findLanguoid = (code: string): Languoid | undefined =>
  codeIndex.get(code);

/**
 * Whether `code` is one of the *shipped* (seed) languoids. NOTE: this is NOT
 * the runtime authority — the live catalog lives in the DB and is extended at
 * runtime. Use it only for client-side name fallbacks and seed import helpers,
 * never to gate which codes are valid (that's `isWellFormedLanguoidCode` for
 * shape, plus a DB lookup server-side).
 */
export const isKnownLanguoid = (code: string): boolean =>
  codeIndex.has(code);

/**
 * Shape of a languoid code: ISO 639-3 (3 lower) + "_" + ISO 15924 script
 * (Title-case), with an optional trailing `_<suffix>` to disambiguate a variety
 * (e.g. `eng_Latn_aust1313` for Australian English).
 */
export const LANGUOID_CODE_PATTERN = /^[a-z]{3}_[A-Z][a-z]{3}(?:_[a-z0-9]+)*$/;

/** Whether `code` is well-formed (`xxx_Yyyy`), regardless of catalog membership. */
export const isWellFormedLanguoidCode = (code: string): boolean =>
  LANGUOID_CODE_PATTERN.test(code);

/**
 * Look up the full English name for a code; useful when constructing
 * Gemini prompts where we substitute `{source_language}` and
 * `{target_language}` with full names rather than codes.
 */
export const languoidEnglishName = (code: string): string | undefined =>
  findLanguoid(code)?.englishName;
